<template>
  <div class="news-center-page">
    <header class="page-header">
      <div class="container">
        <div class="breadcrumb-section">
          <nav class="breadcrumb" v-if="!searchKeyword">
            <a href="/">首页</a>
            <span v-for="(crumb, index) in breadcrumbList" :key="crumb.id">
              > <a href="#" @click.prevent="filterByCategory(crumb.id)">{{ crumb.name }}</a>
            </span>
          </nav>
          <h2 class="current-category-title" v-if="!searchKeyword">{{ currentCategoryName }}</h2>
          <h2 class="current-category-title" v-else>全站搜索结果 - "{{ searchKeyword }}"</h2>
        </div>
        <div class="search-box">
          <input type="text" v-model="searchKeyword" :placeholder="searchKeyword ? '搜索全站文章' : '搜索本栏目文章'" @keyup.enter="doSearch" />
          <button @click="doSearch">搜索</button>
        </div>
      </div>
    </header>
    
    <main class="container page-container">
      <aside class="sidebar">
        <div class="sidebar-section">
          <div class="image-news-list">
            <div 
              v-for="(item, idx) in imageNewsList" 
              :key="idx" 
              class="image-news-item"
              @click="goToArticle(item)"
            >
              <img :src="item.image" :alt="item.title" />
              <span class="image-news-title">{{ item.title }}</span>
              <span class="image-news-date">{{ item.date }}</span>
            </div>
          </div>
        </div>
      </aside>
      
      <div class="main-content">
        <div class="article-list">
          <template v-for="article in articles">
            <div v-if="article.external_link" :key="'ext-'+article.id" class="article-item" @click="handleExternalLink(article)">
              <span class="article-bullet">•</span>
              <span class="article-title">{{ article.title }}</span>
              <span class="article-date">{{ article.date }}</span>
            </div>
            <div v-else :key="'int-'+article.id" class="article-item" @click="goToArticle(article)">
              <span class="article-bullet">•</span>
              <span class="article-title">{{ article.title }}</span>
              <span class="article-date">{{ article.date }}</span>
            </div>
          </template>
        </div>
        
        <div class="pagination-wrapper">
          <span class="total-text">共 {{ totalItems }} 条</span>
          <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">&lt;</button>
          
          <template v-if="totalPages <= 7">
            <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
              {{ page }}
            </button>
          </template>
          
          <template v-else>
            <template v-if="currentPage <= 4">
              <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
                {{ page }}
              </button>
              <span class="ellipsis">...</span>
              <button class="page-btn" @click="goToPage(totalPages)">{{ totalPages }}</button>
            </template>
            
            <template v-else-if="currentPage >= totalPages - 3">
              <button class="page-btn" @click="goToPage(1)">1</button>
              <span class="ellipsis">...</span>
              <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
                {{ page }}
              </button>
            </template>
            
            <template v-else>
              <button class="page-btn" @click="goToPage(1)">1</button>
              <span class="ellipsis">...</span>
              <button v-for="page in visiblePages.slice(2, 5)" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
                {{ page }}
              </button>
              <span class="ellipsis">...</span>
              <button class="page-btn" @click="goToPage(totalPages)">{{ totalPages }}</button>
            </template>
          </template>
          
          <button class="page-btn" :disabled="currentPage >= totalPages" @click="nextPage">&gt;</button>
          <select class="page-size-select" v-model="pageSize" @change="onPageSizeChange">
            <option value="10">10 条/页</option>
            <option value="20">20 条/页</option>
            <option value="30">30 条/页</option>
          </select>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'NewsCenter',
  data() {
    return {
      articles: [],
      imageNewsList: [],
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      totalPages: 0,
      categories: [],
      flatCategories: [],
      activeCategoryId: 2,
      expandedCats: [],
      defaultRootCat: { id: 2, name: '新闻中心' },
      searchKeyword: ''
    }
  },
  computed: {
    breadcrumbList() {
      return this.getBreadcrumbPath(this.activeCategoryId)
    },
    currentCategoryName() {
      const cat = this.flatCategories.find(c => c.id === this.activeCategoryId)
      if (cat && cat.name) {
        return cat.name
      }
      return this.defaultRootCat.name
    },
    visiblePages() {
      const pages = []
      if (this.totalPages <= 7) {
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i)
        }
      } else if (this.currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i)
        }
      } else if (this.currentPage >= this.totalPages - 3) {
        for (let i = this.totalPages - 4; i <= this.totalPages; i++) {
          pages.push(i)
        }
      } else {
        for (let i = this.currentPage - 2; i <= this.currentPage + 2; i++) {
          pages.push(i)
        }
      }
      return pages
    }
  },
  mounted() {
    this.fetchCategories().then(() => {
      this.fetchArticles()
    })
    this.fetchImageNews()
  },
  methods: {
    async fetchArticles() {
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize
        }
        if (!this.searchKeyword) {
          params.category_id = this.activeCategoryId
        }
        if (this.searchKeyword) {
          params.keyword = this.searchKeyword
        }
        const response = await axios.get('/api/posts', { params })
        if (response.data.success) {
          this.articles = response.data.data
          this.totalItems = response.data.total
          this.totalPages = Math.ceil(this.totalItems / this.pageSize)
        }
      } catch (error) {
        console.error('获取文章列表失败:', error)
      }
    },
    doSearch() {
      this.currentPage = 1
      if (this.searchKeyword.trim()) {
        this.$router.push({ path: '/news', query: { keyword: this.searchKeyword.trim() } })
      } else {
        this.$router.push({ path: '/news' })
      }
    },
    async fetchCategories() {
      try {
        const response = await axios.get('/api/categories')
        if (response.data && response.data.success) {
          this.categories = Array.isArray(response.data.data) ? response.data.data : []
          this.flatCategories = Array.isArray(response.data.flat) ? response.data.flat : []
        } else {
          this.categories = []
          this.flatCategories = []
        }
        
        if (this.flatCategories.length === 0) {
          this.flatCategories = this.extractFlatCategories(this.categories)
        }
        
        this.expandAncestors(this.activeCategoryId)
      } catch (error) {
        console.error('获取分类失败:', error)
        this.categories = [
          { id: 2, name: '新闻中心', count: 10, children: [] },
          { id: 3, name: '公告栏', count: 5, children: [] }
        ]
        this.flatCategories = [
          { id: 2, name: '新闻中心', parent: 0 },
          { id: 3, name: '公告栏', parent: 0 }
        ]
      }
    },
    extractFlatCategories(cats, parentId = 0) {
      const result = []
      const arr = Array.isArray(cats) ? cats : []
      arr.forEach(cat => {
        if (cat) {
          result.push({
            id: (cat.id !== undefined && cat.id !== null) ? cat.id : (cat.ID !== undefined && cat.ID !== null ? cat.ID : 0),
            name: (cat.name || '').toString(),
            parent: parentId
          })
          if (cat.children && Array.isArray(cat.children)) {
            result.push(...this.extractFlatCategories(cat.children, cat.id || 0))
          }
        }
      })
      return result
    },
    getBreadcrumbPath(catId) {
      const path = []
      let currentId = catId
      
      const catMap = {}
      this.flatCategories.forEach(c => {
        catMap[c.id] = c
      })
      
      while (currentId && currentId !== 0) {
        const cat = catMap[currentId]
        if (cat) {
          path.unshift({ id: cat.id, name: cat.name })
          currentId = cat.parent
        } else {
          break
        }
      }
      
      if (path.length === 0) {
        path.push(this.defaultRootCat)
      }
      
      return path
    },
    expandAncestors(catId) {
      const catMap = {}
      this.flatCategories.forEach(c => {
        catMap[c.id] = c
      })
      
      let currentId = catId
      while (currentId && currentId !== 0) {
        if (!this.expandedCats.includes(currentId)) {
          this.expandedCats.push(currentId)
        }
        const cat = catMap[currentId]
        if (!cat) break
        currentId = cat.parent
      }
    },
    filterByCategory(categoryId) {
      this.searchKeyword = ''
      this.activeCategoryId = categoryId
      this.expandAncestors(categoryId)
      this.currentPage = 1
      this.$router.push({ path: '/news', query: { category: categoryId } })
    },
    async fetchImageNews() {
      try {
        const response = await axios.get('/api/image-news')
        if (response.data.success) {
          this.imageNewsList = response.data.data.slice(0, 5)
        }
      } catch (error) {
        console.error('获取图片新闻失败:', error)
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.fetchArticles()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.fetchArticles()
      }
    },
    goToPage(page) {
      this.currentPage = page
      this.fetchArticles()
    },
    onPageSizeChange() {
      this.currentPage = 1
      this.fetchArticles()
    },
    goToArticle(item) {
      if (item.external_link) {
        window.open(item.external_link, '_blank')
      } else {
        this.$router.push('/article/' + item.id)
      }
    },
    handleExternalLink(article) {
      window.open(article.external_link, '_blank')
    }
  }
}
</script>

<style lang="less">
.news-center-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.news-center-page .page-header {
  position: sticky;
  top: 198px;
  z-index: 150;
  background: white;
  border-bottom: 1px solid #eee;
  padding: 15px 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .breadcrumb-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #666;

    a {
      color: #4ecdc4;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    span {
      margin: 0 8px;
      color: #999;
    }
  }

  .current-category-title {
    font-size: 36px;
    font-weight: bold;
    color: #4ecdc4;
    margin: 0;
  }

  .search-box {
    display: flex;
    gap: 10px;
    align-items: center;

    input {
      padding: 8px 15px;
      border: 1px solid #ddd;
      border-radius: 5px;
      font-size: 14px;
      width: 220px;
      outline: none;

      &:focus {
        border-color: #4ecdc4;
      }
    }

    button {
      padding: 8px 20px;
      background: #4ecdc4;
      color: white;
      border: none;
      border-radius: 5px;
      font-size: 14px;
      cursor: pointer;
      transition: opacity 0.3s;

      &:hover {
        opacity: 0.9;
      }
    }
  }
}

.news-center-page .page-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  gap: 30px;
  padding: 30px 20px;
}

.news-center-page .sidebar {
  width: 300px;
  flex-shrink: 0;
}

.news-center-page .sidebar-section {
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.news-center-page .sidebar-title {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  background: linear-gradient(135deg, #e8a87c 0%, #e8a87c 100%);
  padding: 10px 15px;
  margin: -20px -20px 20px -20px;
  border-radius: 10px 10px 0 0;
  text-align: center;
}

.news-center-page .image-news-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.news-center-page .image-news-item {
  cursor: pointer;
  transition: transform 0.2s;
}

.news-center-page .image-news-item:hover {
  transform: translateX(5px);
}

.news-center-page .image-news-item img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
}

.news-center-page .image-news-title {
  display: block;
  font-size: 14px;
  color: #333;
  margin-top: 8px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.news-center-page .image-news-date {
  display: block;
  font-size: 12px;
  color: #999;
  margin-top: 5px;
}

.news-center-page .main-content {
  flex: 1;
}

.news-center-page .article-list {
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 10px;
  padding: 15px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.news-center-page .article-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px dashed #eee;
  cursor: pointer;
  transition: background 0.2s;
}

.news-center-page .article-item:last-child {
  border-bottom: none;
}

.news-center-page .article-item:hover {
  background: #f8f9fa;
}

.news-center-page .article-item:hover .article-title {
  color: #4ecdc4;
}

.news-center-page .article-bullet {
  color: #00AEAA;
  font-size: 20px;
  margin-right: 12px;
  line-height: 1;
}

.news-center-page .article-title {
  flex: 1;
  font-size: 16px;
  color: #333;
  line-height: 1.4;
  transition: color 0.2s;
}

.news-center-page .article-date {
  font-size: 14px;
  color: #999;
  margin-left: 20px;
  flex-shrink: 0;
}

.news-center-page .pagination-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6px;
  margin-top: 30px;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.news-center-page .total-text {
  font-size: 14px;
  color: #333;
  margin-right: 10px;
  line-height: 1.4;
}

.news-center-page .page-btn {
  width: 38px;
  height: 32px;
  background: white;
  color: #333;
  border: 1px solid #eee;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.news-center-page .page-btn:hover:not(:disabled) {
  background: #4ecdc4;
  color: white;
  border-color: #4ecdc4;
}

.news-center-page .page-btn.active {
  background: #4ecdc4;
  color: white;
  border-color: #4ecdc4;
}

.news-center-page .page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.news-center-page .ellipsis {
  color: #999;
  padding: 0 5px;
}

.news-center-page .page-size-select {
  margin-left: 10px;
  padding: 6px 10px;
  border: 1px solid #eee;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
}

@media (max-width: 992px) {
  .news-center-page .page-container {
    flex-direction: column;
  }
  .news-center-page .sidebar {
    width: 100%;
  }
}
</style>
